import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Grid,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { Add, Delete, Close, Inventory } from "@mui/icons-material";

const PutOnHoldPopup = ({ open, handleClose, ticketId, onSave }) => {
  const [serialNumber, setSerialNumber] = useState("");
  const [partDetails, setPartDetails] = useState([
    { part_name: "", part_technician_prize: "", additional_charges: "" },
  ]);

  const handleAddPart = () => {
    setPartDetails([
      ...partDetails,
      { part_name: "", part_technician_prize: "", additional_charges: "" },
    ]);
  };

  const handleRemovePart = (index) => {
    if (partDetails.length > 1) {
      const updatedParts = partDetails.filter((_, i) => i !== index);
      setPartDetails(updatedParts);
    }
  };

  const handlePartChange = (index, field, value) => {
    const updatedParts = partDetails.map((part, i) =>
      i === index ? { ...part, [field]: value } : part
    );
    setPartDetails(updatedParts);
  };

  const handleSubmit = () => {
    const filteredParts = partDetails
      .filter((part) => part.part_name.trim() !== "")
      .map((part) => ({
        ...part,
        part_technician_prize: parseInt(part.part_technician_prize) || 0,
        additional_charges: parseInt(part.additional_charges) || 0,
      }));

    if (filteredParts.length === 0) {
      alert("Please add at least one part detail");
      return;
    }

    const payload = {
      ticket_obj_id: ticketId,
      serial_number: serialNumber,
      on_hold_part_details: {
        part_details: filteredParts,
      },
    };

    onSave(payload);
  };

  const handleReset = () => {
    setSerialNumber("");
    setPartDetails([
      { part_name: "", part_technician_prize: "", additional_charges: "" },
    ]);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "bg-white rounded-lg shadow-xl",
      }}
    >
      {/* Header */}
      <DialogTitle className="border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Inventory className="text-blue-600" />
            </div>
            <div>
              <Typography variant="h6" className="font-semibold text-gray-900">
                Put Request On Hold
              </Typography>
              {/* <Chip
                label={`Ticket #${ticketId}`}
                size="small"
                className="bg-blue-50 text-blue-700 font-medium"
              /> */}
            </div>
          </div>
          <IconButton
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            size="small"
          >
            <Close />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className="p-6 space-y-6">
        {/* Serial Number */}
        <Box>
          <Typography
            variant="subtitle1"
            className="font-medium text-gray-700 mb-2"
          >
            Serial Number *
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter equipment serial number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            size="small"
            className="bg-white"
          />
        </Box>

        {/* Part Details Section */}
        <Box>
          <div className="flex justify-between items-center mb-4">
            <Typography
              variant="subtitle1"
              className="font-medium text-gray-700"
            >
              Part Details *
            </Typography>
            <Button
              startIcon={<Add />}
              onClick={handleAddPart}
              variant="outlined"
              size="small"
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              Add Part
            </Button>
          </div>

          <div className="space-y-3">
            {partDetails.map((part, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-3 bg-gray-50/50"
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Part Name"
                      value={part.part_name}
                      onChange={(e) =>
                        handlePartChange(index, "part_name", e.target.value)
                      }
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Technician Price (₹)"
                      type="number"
                      value={part.part_technician_prize}
                      onChange={(e) =>
                        handlePartChange(
                          index,
                          "part_technician_prize",
                          e.target.value
                        )
                      }
                      size="small"
                      required
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Additional Charges (₹)"
                      type="number"
                      value={part.additional_charges}
                      onChange={(e) =>
                        handlePartChange(
                          index,
                          "additional_charges",
                          e.target.value
                        )
                      }
                      size="small"
                      required
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2} className="text-center">
                    <IconButton
                      onClick={() => handleRemovePart(index)}
                      disabled={partDetails.length === 1}
                      size="small"
                      className={`${
                        partDetails.length === 1
                          ? "text-gray-300"
                          : "text-red-500 hover:bg-red-50"
                      }`}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </div>
            ))}
          </div>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions className="border-t border-gray-200 px-6 py-4 bg-gray-50">
        <div className="flex space-x-3 w-full justify-end">
          <Button
            onClick={handleReset}
            variant="outlined"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Reset
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!serialNumber.trim()}
            variant="contained"
            className={`${
              !serialNumber.trim()
                ? "bg-gray-300 text-gray-500"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            Put On Hold
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default PutOnHoldPopup;
